'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { VC_LIST_PREVIEW, LIST_VERIFIED } from '@/lib/free-vc-list-preview';

/**
 * Crawlable preview of the 50 firms in the gated PDF. Statically rendered, so
 * the rows are in the raw HTML — before this, the page that ranks for "free vc"
 * showed crawlers none of the list. Firm, location and focus only: the contact
 * route and check size stay in the PDF, which is what the email gate hands
 * over. Firm names and focus tags are data, not translated.
 */
export function FreeVcListTable() {
  const { t, i18n } = useTranslation();
  const verified = new Date(`${LIST_VERIFIED}T00:00:00Z`).toLocaleDateString(i18n.language || 'en', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return (
    <section className="container pb-16" aria-labelledby="vc-list-preview-heading">
      <div className="max-w-3xl mx-auto">
        <h2 id="vc-list-preview-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          {t('pageContent.freeVcTable.heading')}
        </h2>
        <p className="text-gray-600 mb-6">{t('pageContent.freeVcTable.intro', { date: verified })}</p>
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">{t('pageContent.freeVcTable.firm')}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{t('pageContent.freeVcTable.focus')}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{t('pageContent.freeVcTable.location')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {VC_LIST_PREVIEW.map((r) => (
                <tr key={r.firm}>
                  <th scope="row" className="px-4 py-3 font-medium text-gray-900">{r.firm}</th>
                  <td className="px-4 py-3 text-gray-700">{r.focus.join(', ')}</td>
                  <td className="px-4 py-3 text-gray-700">{r.location ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-gray-600">
          {t('pageContent.freeVcTable.fitPrefix')}{' '}
          <Link href="/tools/vc-fit-checker" className="text-primary-600 hover:text-primary-700 underline">
            {t('pageContent.freeVcTable.fitLink')}
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
